import classNames from 'classnames';
import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import { Container, Grid, Cell } from 'components/Grid';
import Theme from 'styles/theme';

const THERMOMETER_BORDER_RADIUS = 11;
const INDICATOR_BORDER_RADIUS = 25;

class SecondaryNavMobile extends React.PureComponent {
  constructor(props) {
    super(props);
    this.tabRefs = map(props.slides, () => React.createRef());
    this.state = { tabIndicatorWidth: 0, tabIndicatorOffsetLeft: 0 };
  }

  componentDidMount() {
    this.updateActiveSlide();
  }

  componentDidUpdate(prevProps) {
    if (this.props.activeSlideIndex !== prevProps.activeSlideIndex) {
      this.updateActiveSlide();
    }
  }

  setTabState = () => {
    const activeTabRef = this.tabRefs[this.props.activeSlideIndex];
    const tabIndicatorOffsetLeft = get(activeTabRef, 'current.offsetLeft', 0);
    const tabIndicatorWidth = get(activeTabRef, 'current.clientWidth', 0);

    this.setState({ tabIndicatorOffsetLeft, tabIndicatorWidth });
  };

  scrollToActiveHeader = index => {
    const activeChild = this.tabRefs[index];
    const containerElement = document.querySelector('.SecondaryNavMobile__tabs-container');

    if (!activeChild || !containerElement) return;

    // find half width of viewport
    const centerViewPoint = containerElement.clientWidth / 2;

    // find half of elements length
    const centerElementLength = activeChild.current.clientWidth / 2;

    // subtract half of the elements length from 50% of screen size.
    // this will give an offsetLeft that will center the element
    const adjustedCenter = centerViewPoint - centerElementLength;
    // set the offsetLeft to be the adjusted offset left

    containerElement.scrollLeft = activeChild.current.offsetLeft - adjustedCenter + 24;
  };

  updateActiveSlide = () => {
    this.setTabState();
    this.scrollToActiveHeader(this.props.activeSlideIndex);
  };

  onSlideSelect = index => {
    this.props.onClick(index);
  };

  getFadePercentages = () => {
    const { slides, activeSlideIndex } = this.props;

    // Visual edge cases
    if (activeSlideIndex === 0) return 0;

    const fadePercentage = 100 / slides.length;
    const currentFade = fadePercentage * activeSlideIndex;

    return currentFade;
  };

  render() {
    const { slides, activeSlideIndex, isInverted } = this.props;

    return (
      <div
        className={classNames('SecondaryNavMobile', {
          'SecondaryNavMobile--inverted': isInverted === true,
        })}
      >
        <div className="SecondaryNavMobile__thermometer">
          {map(slides, () => {
            return <div className="SecondaryNavMobile__thermometer-tick-mark" />;
          })}
        </div>
        <div className="SecondaryNavMobile__thermometer-overlay" />
        <div className="SecondaryNavMobile__tabs-affix">
          <Container className="SecondaryNavMobile__tabs-container">
            <Grid>
              <Cell smallUp={{ columns: 12 }}>
                <div className="SecondaryNavMobile__tabs">
                  {map(slides, (slide, index) => {
                    const buttonClasses = classNames(
                      'SecondaryNavMobile__tab',
                      'SecondaryNavMobile__tab-button',
                      {
                        'SecondaryNavMobile__tab-button--active': index === activeSlideIndex,
                        'SecondaryNavMobile__tab-button--inverted': isInverted === true,
                      }
                    );

                    return (
                      <button
                        className={buttonClasses}
                        onClick={() => this.onSlideSelect(index)}
                        key={`nav-${index}`}
                        ref={this.tabRefs[index]}
                      >
                        {slide}
                      </button>
                    );
                  })}
                  <div
                    className={classNames('SecondaryNavMobile__tabs-indicator', {
                      'SecondaryNavMobile__tabs-indicator--inverted': isInverted === true,
                    })}
                    style={{
                      left: this.state.tabIndicatorOffsetLeft,
                      width: this.state.tabIndicatorWidth,
                    }}
                  />
                </div>
              </Cell>
            </Grid>
          </Container>
        </div>

        <style jsx>
          {`
            .SecondaryNavMobile {
              position: fixed;
              z-index: 50;
              display: flex;
              flex-direction: column;
              width: 100%;
              background-color: rgb(77, 85, 89, 0.85);
            }

            .SecondaryNavMobile--inverted {
              background-color: rgb(255, 255, 255, 0.85);
            }

            .SecondaryNavMobile__thermometer {
              display: flex;
              justify-content: space-evenly;
              align-items: flex-end;
              min-height: ${Theme.get('spacing') * 2}px;
              width: 90%;
              border-radius: ${THERMOMETER_BORDER_RADIUS}px;
              align-self: center;
              margin-top: ${Theme.get('spacing') * 2}px;
              margin-bottom: ${Theme.get('spacing') * 3}px;
              background: linear-gradient(
                to right,
                #ea8689 10%,
                #e9ab75 25%,
                #b8da96 40%,
                #80b3c0 57%,
                #7786c0 73%,
                #8f86b8 89%
              );
              box-shadow: 0 8px 14px 0 rgb(0 0 0 / 18%);
            }

            .SecondaryNavMobile__thermometer-overlay {
              position: absolute;
              width: 90%;
              height ${Theme.get('spacing') * 2}px;
              top: ${Theme.get('spacing') * 2}px;
              left: ${Theme.get('spacing') * 2.5}px;
              border-radius: ${THERMOMETER_BORDER_RADIUS}px;
              background: linear-gradient(
                to right,
                rgba(244, 244, 244, 1) ${this.getFadePercentages() - 10}%,
                rgba(244, 244, 244, 0) ${this.getFadePercentages()}%
              );
            }

            .SecondaryNavMobile__thermometer-tick-mark {
              background-color: ${Theme.get('colors-white')};
              width: 1px;
              height: ${Theme.get('spacing')}px;
            }

            .SecondaryNavMobile__tabs-affix {
              overflow-x: scroll;
            }

            .SecondaryNavMobile__tabs {
              display: flex;
            }

            .SecondaryNavMobile__tabs::after {
              content: '';
              min-width: 6px;
            }

            .SecondaryNavMobile__tab {
              display: inline-block;
              width: auto;
              margin-right: ${Theme.get('spacing') * 6}px;
              white-space: nowrap;
            }

            .SecondaryNavMobile__tabs-indicator {
              position: absolute;
              left: 0;
              bottom: 0px;
              height: 2px;
              border-radius: ${INDICATOR_BORDER_RADIUS}px;
              background-color: ${Theme.get('colors-white')};
            }

            .SecondaryNavMobile__tabs-indicator--inverted {
              background-color: ${Theme.get('colors-black')};
            }
          `}
        </style>

        <style jsx global>
          {`
            .Page.Banner--enabled.Banner--visible .SecondaryNavMobile {
              transform: translateY(0);
              transition: transform ${Theme.get('transition-duration-fast-ms')}
                ${Theme.get('transition-timing-ease-out')};
            }

            body.Banner--enabled:not(.Banner--visible) .SecondaryNavMobile {
              transform: translateY(-${Theme.get('banner-height')}px);
              transition-duration: ${Theme.get('transition-duration-ms')};
            }

            .SecondaryNavMobile__tab-button {
              display: block;
              appearance: none;
              color: ${Theme.get('colors-white')};
              font-size: ${Theme.get('font-size-small-px')};
              font-weight: ${Theme.get('font-weight-bold')};
              text-transform: uppercase;
              font-stretch: ${Theme.get('font-style-normal')};
              font-style: ${Theme.get('font-style-normal')};
              margin-bottom: ${Theme.get('spacing') / 2}px;
              text-decoration: none;
              letter-spacing: 2px;
              background: none;
              border: none;
              outline: inherit;
              cursor: pointer;
              padding: 0;
              width: 50px;
            }

            .SecondaryNavMobile__tab-button--inverted {
              color: ${Theme.get('colors-black')};
            }

            .SecondaryNavMobile__tabs-container {
              overflow-x: scroll;
              scroll-behavior: smooth;
              -ms-overflow-style: none; /* IE and Edge */
              scrollbar-width: none; /* Firefox */
            }

            /* Hide scrollbar for Chrome, Safari and Opera */
            .SecondaryNavMobile__tabs-container::-webkit-scrollbar {
              display: none;
            }
          `}
        </style>
      </div>
    );
  }
}

SecondaryNavMobile.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
  activeSlideIndex: PropTypes.number,
  onClick: PropTypes.func,
  isInverted: PropTypes.bool,
};

SecondaryNavMobile.defaultProps = {
  activeSlideIndex: 0,
  onClick: () => {},
  isInverted: false,
};

export default SecondaryNavMobile;
